import { useEffect, useState } from 'react';

export interface IVisibleViewport {
  /** Height of what's on screen right now (CSS px). */
  height: number;
  /** How far that is scrolled down from the top of the page's layout. */
  offsetTop: number;
}

/**
 * The part of the page that's actually visible — window.visualViewport,
 * which shrinks for Safari's toolbars and for the on-screen keyboard.
 *
 * iOS Safari's `100vh` is the height with its toolbars hidden, and the
 * keyboard slides over the page instead of resizing it, so a panel sized in
 * vh put the chat input under the toolbar / keyboard (UAT 2026-10-08, sheet
 * row 15: "The chat field was too far down the page and it was a little hard
 * to see the output and type a response"). Null where the API doesn't exist.
 */
export default function useVisibleViewport(): IVisibleViewport | null {
  const [viewport, setViewport] = useState<IVisibleViewport | null>(null);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;
    const update = () => setViewport({ height: vv.height, offsetTop: vv.offsetTop });
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return viewport;
}
